import { useState } from "react";
import type { CadInstance } from "../../bimgo";
import type { HostsView } from "../../hooks/useHosts";
import type { PluginsController } from "../../hooks/usePlugins";
import { useLocale } from "../../i18n/LocaleProvider";
import { CATALOG, pluginView, updateTier, type PluginId } from "../../lib/plugins";
import { PluginManageDialog } from "../plugins/manage";

const EMPTY: CadInstance[] = [];

/** Update discovery on the new-chat home, using the same plans as Plugins. */
export function PluginUpdateCards({ hosts, plugins }: {
  hosts: HostsView;
  plugins: PluginsController;
}) {
  const { t } = useLocale();
  const [manageId, setManageId] = useState<PluginId | null>(null);
  const instances = hosts.kind === "loaded" ? hosts.instances : EMPTY;
  const snapshots = plugins.state.kind === "loaded" ? plugins.state.snapshots : null;

  return (
    <>
      {CATALOG.filter((entry) => entry.available).map((entry) => {
        const view = pluginView(snapshots, instances, entry.id);
        const pending = view?.versions.filter((year) => year.state === "ready" && year.updateTo) ?? [];
        if (!pending.length) return null;
        const rollback = pending.every((year) =>
          updateTier(year.installedPluginVersion ?? null, year.updateTo!) === "downgrade",
        );
        const titleId = `plugin-update-${entry.id}`;
        return (
          <section key={entry.id} className="plugin-launch plugin-update" aria-labelledby={titleId}>
            <div className="plugin-launch-logos" aria-hidden="true">
              <img src={`/integrations/${entry.id}-logo.png`} alt="" width="40" height="40" />
            </div>
            <div className="plugin-launch-copy">
              <h2 id={titleId}>{t(rollback ? "chat.pluginUpdate.rollbackTitle" : "chat.pluginUpdate.title", { host: entry.hostName })}</h2>
              <p>{t("chat.pluginUpdate.description")}</p>
            </div>
            <button
              type="button"
              className="plugin-launch-status plugin-update-action"
              disabled={plugins.busy}
              aria-label={t("chat.pluginUpdate.actionLabel", { host: entry.hostName })}
              onClick={() => { if (!plugins.busy) setManageId(entry.id); }}
            >
              {t("chat.pluginUpdate.action")}
            </button>
          </section>
        );
      })}
      {/* Keep results mounted even after detection clears the last update card. */}
      <PluginManageDialog
        open={manageId !== null}
        onClose={() => setManageId(null)}
        vendor={manageId ?? "revit"}
        plugins={plugins}
        instances={instances}
      />
    </>
  );
}
